import { Button, Result, Spin } from "antd";
import { useEffect, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { useConfirmPayment } from "../../hooks/usePayment";

const getPortOneMessage = (searchParams) =>
  searchParams.get("pgMessage") ||
  searchParams.get("message") ||
  "결제가 완료되지 않았습니다.";

const PaymentSuccessContent = ({
  paymentId,
  amountRaw,
  currency = "KRW",
  isConfirmPaymentPending = false,
}) => {
  const history = useHistory();
  const amount = Number(amountRaw) || 0;
  const amountLabel =
    amountRaw != null && amountRaw !== ""
      ? currency === "KRW"
        ? `${amount.toLocaleString("ko-KR")}원`
        : `${amount.toLocaleString()} ${currency}`
      : "—";

  return (
    <div className="min-h-[calc(100vh-120px)] px-4 py-10 md:py-14">
      <div className="mx-auto w-full max-w-[480px] rounded-[22px] border border-[#e7eaee] bg-white/95 px-5 py-8 shadow-[0_10px_40px_rgba(17,24,39,0.08)] backdrop-blur-sm md:px-8 md:py-10">
        <Result
          status="success"
          title={
            <span className="text-2xl font-bold tracking-tight text-[#0f172a] md:text-3xl">
              결제가 완료되었습니다
            </span>
          }
          subTitle={
            <div className="mx-auto max-w-[360px] text-[15px] leading-relaxed text-[#64748b]">
              <p>승인 처리를 마쳤습니다.</p>
              <p>결제 정보를 확인해주세요.</p>
            </div>
          }
          extra={
            <div className="w-full space-y-5 pt-2">
              <dl className="space-y-0 rounded-2xl border border-[#eef2f6] bg-[#fafbfc] px-4 py-1 text-left text-[14px]">
                <div className="flex flex-col gap-0.5 border-b border-[#eef2f6] py-3 sm:flex-row sm:items-start sm:justify-between sm:gap-4">
                  <dt className="shrink-0 font-semibold text-[#94a3b8]">
                    주문번호
                  </dt>
                  <dd className="break-all text-right font-medium text-[#0f172a] sm:text-left">
                    {paymentId.slice(4) ?? "—"}
                  </dd>
                </div>
                <div className="flex flex-col gap-0.5 py-3 sm:flex-row sm:items-center sm:justify-between sm:gap-4">
                  <dt className="shrink-0 font-semibold text-[#94a3b8]">
                    결제 금액
                  </dt>
                  <dd className="text-right text-[17px] font-bold tabular-nums text-[#3182F6] sm:text-left">
                    {amountLabel}
                  </dd>
                </div>
              </dl>
              <div className="flex">
                <Button
                  type="primary"
                  loading={isConfirmPaymentPending}
                  className="h-11 w-full rounded-xl !bg-[#3182F6] font-semibold shadow-sm"
                  onClick={() =>
                    history.push("/payments/brand-payments-history")
                  }
                >
                  결제 내역으로 이동
                </Button>
              </div>
            </div>
          }
        />
      </div>
    </div>
  );
};

const PaymentFailContent = ({ code, messageText, campaignId }) => {
  const history = useHistory();
  const returnUrl = campaignId
    ? `/campaign/manage?campaign_id=${encodeURIComponent(campaignId)}`
    : "/campaign/manage";

  return (
    <div className="min-h-[calc(100vh-120px)] px-4 py-10 md:py-14">
      <div className="mx-auto w-full max-w-[480px] rounded-[22px] border border-[#fecaca] bg-white/95 px-5 py-8 shadow-[0_10px_40px_rgba(220,38,38,0.08)] backdrop-blur-sm md:px-8 md:py-10">
        <Result
          status="error"
          title={
            <span className="text-2xl font-bold text-[#0f172a] md:text-3xl">
              결제에 실패했습니다
            </span>
          }
          subTitle={
            <div className="mx-auto max-w-[360px] text-lg md:text-xl leading-relaxed text-[#64748b]">
              <p>결제가 완료되지 않았습니다.</p>
              <p>다시 시도해 주세요.</p>
            </div>
          }
          extra={
            <div className="w-full space-y-5 pt-2">
              <dl className="space-y-0 rounded-2xl border border-[#fee2e2] bg-[#fffafa] px-4 py-1 text-left text-[14px]">
                <div className="flex flex-col gap-0.5 border-b border-[#fee2e2] py-3 sm:flex-row sm:items-start sm:justify-between sm:gap-4">
                  <dt className="shrink-0 font-semibold text-[#94a3b8]">
                    에러 코드
                  </dt>
                  <dd className="break-all text-right font-mono text-[13px] font-medium text-[#b91c1c] sm:text-left">
                    {code || "—"}
                  </dd>
                </div>
                <div className="flex flex-col gap-0.5 py-3 sm:flex-row sm:items-start sm:justify-between sm:gap-4">
                  <dt className="shrink-0 font-semibold text-[#94a3b8]">
                    실패 사유
                  </dt>
                  <dd className="text-right text-[#0f172a] sm:text-left">
                    {messageText || "사유를 확인할 수 없습니다."}
                  </dd>
                </div>
              </dl>
              <div className="flex">
                <Button
                  className="h-11 flex-1 rounded-xl font-semibold"
                  onClick={() => history.replace(returnUrl)}
                  type="primary"
                  danger
                >
                  돌아가기
                </Button>
              </div>
            </div>
          }
        />
      </div>
    </div>
  );
};

const PaymentAlreadyProcessedContent = ({ paymentId }) => {
  const history = useHistory();

  return (
    <div className="min-h-[calc(100vh-120px)] px-4 py-10 md:py-14">
      <div className="mx-auto w-full max-w-[480px] rounded-[22px] border border-[#e7eaee] bg-white/95 px-5 py-8 shadow-[0_10px_40px_rgba(17,24,39,0.08)] backdrop-blur-sm md:px-8 md:py-10">
        <Result
          status="warning"
          title={
            <span className="text-2xl font-bold text-[#0f172a] md:text-3xl">
              이미 처리된 결제입니다
            </span>
          }
          subTitle={
            <div className="mx-auto max-w-[360px] text-[15px] leading-relaxed text-[#64748b]">
              <p>결제 진행 정보가 만료되었거나 이미 처리되었습니다.</p>
              <p>결제 내역에서 상태를 확인해주세요.</p>
            </div>
          }
          extra={
            <div className="w-full space-y-5 pt-2">
              <dl className="space-y-0 rounded-2xl border border-[#eef2f6] bg-[#fafbfc] px-4 py-1 text-left text-[14px]">
                <div className="flex flex-col gap-0.5 py-3 sm:flex-row sm:items-start sm:justify-between sm:gap-4">
                  <dt className="shrink-0 font-semibold text-[#94a3b8]">
                    주문번호
                  </dt>
                  <dd className="break-all text-right font-medium text-[#0f172a] sm:text-left">
                    {paymentId.slice(4) ?? "—"}
                  </dd>
                </div>
              </dl>
              <div className="flex">
                <Button
                  type="primary"
                  className="h-11 w-full rounded-xl !bg-[#3182F6] font-semibold shadow-sm"
                  onClick={() =>
                    history.push("/payments/brand-payments-history")
                  }
                >
                  결제 내역으로 이동
                </Button>
              </div>
            </div>
          }
        />
      </div>
    </div>
  );
};

export const PaymentReturn = () => {
  const { search } = useLocation();
  const [confirmStatus, setConfirmStatus] = useState("pending");
  const [confirmErrorMessage, setConfirmErrorMessage] = useState("");
  const [confirmedPaymentInfo, setConfirmedPaymentInfo] = useState(null);

  const searchParams = new URLSearchParams(search);

  const paymentId = searchParams.get("paymentId");
  const campaignId = searchParams.get("campaign_id");
  const code = searchParams.get("code");
  const pgCode = searchParams.get("pgCode");
  const message = searchParams.get("message");
  const pgMessage = searchParams.get("pgMessage");
  const errorCode = pgCode || code;
  const hasPaymentError = Boolean(errorCode || pgMessage || message);
  const errorMessage = getPortOneMessage(searchParams);

  const {
    mutateAsync: confirmPaymentMutation,
    isPending: isConfirmPaymentPending,
  } = useConfirmPayment();

  useEffect(() => {
    async function confirmPortOnePayment() {
      if (hasPaymentError || !paymentId || confirmStatus !== "pending") {
        if (hasPaymentError && paymentId) {
          sessionStorage.removeItem(`portone_payment:${paymentId}`);
        }
        return;
      }

      const paymentInfo = JSON.parse(
        sessionStorage.getItem(`portone_payment:${paymentId}`)
      );

      if (!paymentInfo?.id) {
        setConfirmStatus("processed");
        return;
      }

      const finalized = await confirmPaymentMutation({
        id: paymentInfo.id,
        merchant_uid: paymentId,
      });

      if (finalized?.error) {
        sessionStorage.removeItem(`portone_payment:${paymentId}`);
        setConfirmErrorMessage(finalized.error);
        setConfirmStatus("failed");
        return;
      }

      setConfirmedPaymentInfo(paymentInfo);
      sessionStorage.removeItem(`portone_payment:${paymentId}`);
      setConfirmStatus(finalized ? "success" : "failed");
    }

    confirmPortOnePayment();
  }, [confirmPaymentMutation, confirmStatus, hasPaymentError, paymentId]);

  if (hasPaymentError) {
    return (
      <PaymentFailContent
        code={errorCode || "PAYMENT_FAILED"}
        messageText={errorMessage}
        campaignId={campaignId}
      />
    );
  }

  if (!paymentId) {
    return (
      <PaymentFailContent
        code="PAYMENT_ID_MISSING"
        messageText="결제 식별자가 없습니다."
        campaignId={campaignId}
      />
    );
  }

  if (confirmStatus === "failed") {
    return (
      <PaymentFailContent
        code="PAYMENT_CONFIRM_FAILED"
        messageText={
          confirmErrorMessage || "결제는 완료되었지만 승인 처리에 실패했습니다."
        }
        campaignId={campaignId}
      />
    );
  }

  if (confirmStatus === "processed") {
    return <PaymentAlreadyProcessedContent paymentId={paymentId} />;
  }

  if (confirmStatus === "success") {
    return (
      <PaymentSuccessContent
        paymentId={paymentId}
        amountRaw={confirmedPaymentInfo?.amount}
        currency={confirmedPaymentInfo?.currency}
        isConfirmPaymentPending={isConfirmPaymentPending}
      />
    );
  }

  return (
    <div className="min-h-[calc(100vh-120px)] px-4 py-10 md:py-14">
      <div className="mx-auto flex w-full max-w-[480px] flex-col items-center gap-5 rounded-[22px] border border-[#e7eaee] bg-white/95 px-5 py-10 text-center shadow-[0_10px_40px_rgba(17,24,39,0.08)] backdrop-blur-sm md:px-8">
        <Spin spinning={isConfirmPaymentPending} />
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-[#0f172a] md:text-3xl">
            결제 승인 처리 중입니다
          </h1>
          <p className="mt-3 text-[15px] leading-relaxed text-[#64748b]">
            잠시만 기다려주세요.
          </p>
        </div>
      </div>
    </div>
  );
};
